import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  User as UserIcon,
  Check,
  AlertCircle,
  AlertTriangle,
  MailCheck,
  Monitor,
  MapPin,
  LogOut,
  Trash2,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { authApi, type AccountSession, type TrustedDevice } from '@/api/client';
import { AUTH_ME_QUERY_KEY, useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { WorkDescriptionField } from '@os/components/WorkDescriptionField';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;
const TRUSTED_DEVICES_QUERY_KEY = ['auth', 'trusted-devices'] as const;

function initialsOf(name: string | undefined): string {
  if (!name) return 'U';
  return (
    name
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U'
  );
}

export function SettingsAccountSection() {
  const { t } = useTranslation('dashboard');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.name ?? '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [resendState, setResendState] = useState<SaveState>('idle');

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== (user?.name ?? '');

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaveState('saving');
    try {
      await authApi.updateProfile({ name: trimmed });
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
    }
  };

  const handleResend = async () => {
    setResendState('saving');
    try {
      await authApi.resendVerification();
      setResendState('saved');
    } catch {
      setResendState('error');
    }
  };

  const emailConfirmed = user?.emailConfirmed ?? true;

  return (
    <div className="space-y-6">
      {/* ── Profile ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">{t('settings.account.title')}</CardTitle>
          </div>
          <CardDescription>{t('settings.account.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-secondary text-muted-foreground text-sm font-semibold">
                {initialsOf(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || '—'}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email || ''}</p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="settings-name">{t('settings.account.nameLabel')}</Label>
              <Input
                id="settings-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (saveState !== 'idle') setSaveState('idle');
                }}
                autoComplete="name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-email">{t('settings.account.emailLabel')}</Label>
              <Input id="settings-email" value={user?.email ?? ''} disabled />
              <div className="flex flex-wrap items-center gap-2">
                {emailConfirmed ? (
                  <Badge className="bg-cobalt-pale text-cobalt-ink text-xs">
                    <MailCheck className="mr-1 h-3 w-3" aria-hidden="true" />
                    {t('settings.account.verified')}
                  </Badge>
                ) : (
                  <>
                    <Badge variant="outline" className="text-xs">
                      {t('settings.account.notVerified')}
                    </Badge>
                    {resendState === 'saved' ? (
                      <span className="text-xs text-cobalt-ink">
                        {t('settings.account.verificationSent')}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleResend}
                        disabled={resendState === 'saving'}
                      >
                        {t('settings.account.resendVerification')}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('settings.account.emailHint')}</p>
            </div>

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!dirty || saveState === 'saving'}>
                {saveState === 'saving' ? t('settings.saving') : t('settings.save')}
              </Button>
              {saveState === 'saved' && (
                <span
                  role="status"
                  className="inline-flex items-center gap-1 text-sm text-cobalt-ink"
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                  {t('settings.saved')}
                </span>
              )}
              {saveState === 'error' && (
                <span
                  role="alert"
                  className="inline-flex items-center gap-1 text-sm text-destructive"
                >
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  {t('settings.saveError')}
                </span>
              )}
            </div>
          </form>

          {/* "Meilleure description de votre travail ?" — the SAME shared,
              persisted field as Général (one `useWorkDescription` hook keyed on
              the shared preferences query), so editing it here or in Général is a
              single source of truth: each panel reflects the other's change. Its
              own labels come from the `os` namespace. */}
          <WorkDescriptionField idPrefix="settings-account-work" />
        </CardContent>
      </Card>

      {/* L'encart Organisation (nom, type de compte, membre depuis, ID d'org) a
          été retiré : il n'affichait que des valeurs en lecture seule dont
          aucune n'est actionnable par l'opérateur (Notion, Settings → « A
          retirer : organization »).

          Sessions actives et Appareils de confiance ont déménagé dans
          Sécurité — c'est là qu'on va les chercher, et l'onglet Sécurité ne
          portait jusqu'ici qu'un badge « bientôt disponible » à leur sujet. Les
          deux cartes restent définies ici et sont exportées. */}

      {/* ── Zone de danger ──────────────────────────────────── */}
      <DangerZoneCard
        email={user?.email ?? ''}
        onLoggedOut={() => {
          logout();
          navigate('/login');
        }}
      />
    </div>
  );
}

/* ─── Active sessions ─────────────────────────────────────── */

export function ActiveSessionsCard() {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => (await authApi.listSessions()).data,
  });

  const sessions = data?.sessions ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Monitor className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{t('settings.account.sessions.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.account.sessions.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('settings.account.sessions.loading')}
          </p>
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('settings.account.sessions.error')}
          </p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('settings.account.sessions.empty')}</p>
        ) : (
          <div data-testid="account-sessions" className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.sessions.colDevice')}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.sessions.colLocation')}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.sessions.colCreated')}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.sessions.colLastUsed')}
                  </th>
                  <th className="py-2 pl-3 text-right font-medium">
                    {t('settings.account.sessions.colAction')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s: AccountSession) => (
                  <SessionRow key={s.id} session={s} onRevoked={invalidate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && !data.deviceTrackingSupported && sessions.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('settings.account.sessions.noDeviceTracking')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One session row. Device / location / last-used come straight from the backend
 * (captured at token issue/rotation) and fall back to an honest "—" when null.
 *
 * Revoking a session OTHER than the current one is a destructive action on
 * another device, so — mirroring "log out of all devices" — it demands the
 * current password (server-side re-auth). The current session is never
 * individually revocable here; use the danger-zone "log out everywhere".
 */
function SessionRow({
  session,
  onRevoked,
}: {
  session: AccountSession;
  onRevoked: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  const revoke = useMutation({
    // Server re-auth: the password is sent and verified backend-side.
    mutationFn: () => authApi.revokeSession(session.id, password),
    onSuccess: () => {
      setConfirming(false);
      setPassword('');
      onRevoked();
    },
  });

  const revokeError = revoke.isError
    ? axios.isAxiosError(revoke.error) && revoke.error.response?.status === 401
      ? t('settings.account.danger.reauthInvalid')
      : t('settings.account.sessions.revokeError')
    : null;

  return (
    <>
      <tr data-testid={`session-row-${session.id}`} className="border-b last:border-0">
        <td className="py-2 pr-3">
          <span className="inline-flex items-center gap-2">
            <Monitor className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className={session.device ? undefined : 'text-muted-foreground'}>
              {session.device ?? t('settings.account.sessions.deviceUnknown')}
            </span>
            {session.current && (
              <Badge variant="outline" className="text-[10px]">
                {t('settings.account.sessions.current')}
              </Badge>
            )}
          </span>
        </td>
        <td className="py-2 pr-3 text-muted-foreground">
          {session.location ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{session.location}</span>
            </span>
          ) : (
            // Honest fallback: no fabricated city. The coarse /24 (if any) sits in
            // the title so it's available on hover without being the primary text.
            <span title={session.ipPrefix ?? undefined}>
              {t('settings.account.sessions.locationUnknown')}
            </span>
          )}
        </td>
        <td className="py-2 pr-3 text-muted-foreground">
          {new Date(session.createdAt).toLocaleString()}
        </td>
        <td className="py-2 pr-3 text-muted-foreground">
          {session.lastUsedAt ? new Date(session.lastUsedAt).toLocaleString() : '—'}
        </td>
        <td className="py-2 pl-3 text-right">
          {session.current ? (
            <span className="text-xs text-muted-foreground">
              {t('settings.account.sessions.currentShort')}
            </span>
          ) : (
            !confirming && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:text-destructive"
                data-testid={`session-revoke-${session.id}`}
                onClick={() => setConfirming(true)}
              >
                {t('settings.account.sessions.revoke')}
              </Button>
            )
          )}
        </td>
      </tr>
      {confirming && !session.current && (
        <tr data-testid={`session-revoke-form-${session.id}`}>
          <td colSpan={5} className="pb-3">
            <div className="space-y-2 rounded-control border border-destructive/40 bg-destructive/5 p-3">
              <Label htmlFor={`session-revoke-password-${session.id}`} className="text-xs">
                {t('settings.account.sessions.revokeConfirmPrompt')}
              </Label>
              <Input
                id={`session-revoke-password-${session.id}`}
                data-testid={`session-revoke-password-${session.id}`}
                type="password"
                autoComplete="current-password"
                value={password}
                placeholder={t('settings.account.danger.reauthPlaceholder')}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setConfirming(false);
                    setPassword('');
                  }}
                  disabled={revoke.isPending}
                >
                  {t('settings.account.danger.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  data-testid={`session-revoke-confirm-${session.id}`}
                  onClick={() => revoke.mutate()}
                  disabled={password.length === 0 || revoke.isPending}
                >
                  {revoke.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    t('settings.account.sessions.revoke')
                  )}
                </Button>
              </div>
              {revokeError && (
                <p role="alert" className="text-xs text-destructive">
                  {revokeError}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ─── Trusted devices ("remember this device") ────────────── */

/**
 * Real trusted-devices surface. A trusted device is an opaque, cookie-backed
 * recognition marker for one browser — DISTINCT from an active session above
 * (which is a revocable login). It lists the user's trusted devices with the
 * current one flagged, lets the operator trust the current browser when it isn't
 * yet, and remove trust from any device (which truly revokes it server-side —
 * the cookie token stops matching anything). No fabricated data: an unrecognised
 * User-Agent shows an honest placeholder label.
 */
export function TrustedDevicesCard() {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: TRUSTED_DEVICES_QUERY_KEY,
    queryFn: async () => (await authApi.listTrustedDevices()).data,
  });

  const devices = data?.devices ?? [];
  const currentTrusted = data?.currentDeviceTrusted ?? false;
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: TRUSTED_DEVICES_QUERY_KEY });

  const trust = useMutation({
    mutationFn: () => authApi.trustCurrentDevice(),
    onSuccess: invalidate,
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{t('settings.account.trustedDevices.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.account.trustedDevices.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('settings.account.trustedDevices.loading')}
          </p>
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('settings.account.trustedDevices.error')}
          </p>
        ) : devices.length === 0 ? (
          <p data-testid="trusted-devices-empty" className="text-sm text-muted-foreground">
            {t('settings.account.trustedDevices.empty')}
          </p>
        ) : (
          <div data-testid="trusted-devices" className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.trustedDevices.colDevice')}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.trustedDevices.colAdded')}
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    {t('settings.account.trustedDevices.colLastSeen')}
                  </th>
                  <th className="py-2 pl-3 text-right font-medium">
                    {t('settings.account.trustedDevices.colAction')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d: TrustedDevice) => (
                  <TrustedDeviceRow key={d.id} device={d} onRemoved={invalidate} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* "Faire confiance à cet appareil" — only when the current browser is
            not yet a trusted device. Trusting it sets the long-lived cookie. */}
        {!isLoading && !isError && !currentTrusted && (
          <div className="space-y-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="trust-current-device"
              onClick={() => trust.mutate()}
              disabled={trust.isPending}
            >
              {trust.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ShieldCheck className="mr-1.5 h-4 w-4" aria-hidden="true" />
              )}
              {t('settings.account.trustedDevices.trustThisDevice')}
            </Button>
            {trust.isError && (
              <p role="alert" className="text-xs text-destructive">
                {t('settings.account.trustedDevices.trustError')}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One trusted-device row. Label / added / last-seen come straight from the
 * backend and fall back to an honest placeholder when null. "Retirer" removes
 * the device server-side, which invalidates its cookie token for good.
 */
function TrustedDeviceRow({
  device,
  onRemoved,
}: {
  device: TrustedDevice;
  onRemoved: () => void;
}) {
  const { t } = useTranslation('dashboard');

  const remove = useMutation({
    mutationFn: () => authApi.removeTrustedDevice(device.id),
    onSuccess: onRemoved,
  });

  return (
    <tr data-testid={`trusted-device-row-${device.id}`} className="border-b last:border-0">
      <td className="py-2 pr-3">
        <span className="inline-flex items-center gap-2">
          <Monitor className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className={device.label ? undefined : 'text-muted-foreground'}>
            {device.label ?? t('settings.account.trustedDevices.deviceUnknown')}
          </span>
          {device.current && (
            <Badge variant="outline" className="text-[10px]">
              {t('settings.account.trustedDevices.current')}
            </Badge>
          )}
        </span>
      </td>
      <td className="py-2 pr-3 text-muted-foreground">
        {new Date(device.createdAt).toLocaleDateString()}
      </td>
      <td className="py-2 pr-3 text-muted-foreground">
        {new Date(device.lastSeenAt).toLocaleString()}
      </td>
      <td className="py-2 pl-3 text-right">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-destructive hover:text-destructive"
          data-testid={`trusted-device-remove-${device.id}`}
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
        >
          {remove.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            t('settings.account.trustedDevices.remove')
          )}
        </Button>
        {remove.isError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {t('settings.account.trustedDevices.removeError')}
          </p>
        )}
      </td>
    </tr>
  );
}

/* ─── Danger zone: logout-all + delete account ────────────── */

function DangerZoneCard({ email, onLoggedOut }: { email: string; onLoggedOut: () => void }) {
  const { t } = useTranslation('dashboard');

  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [logoutPassword, setLogoutPassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  const logoutAll = useMutation({
    // Server re-auth: the current password is sent and verified backend-side.
    mutationFn: () => authApi.logoutAllDevices(logoutPassword),
    onSuccess: onLoggedOut,
  });

  const del = useMutation({
    // Server re-auth: the current password is sent and verified backend-side.
    mutationFn: () => authApi.deleteAccount(deletePassword),
    onSuccess: onLoggedOut,
  });

  const logoutError = logoutAll.isError
    ? axios.isAxiosError(logoutAll.error) && logoutAll.error.response?.status === 401
      ? t('settings.account.danger.reauthInvalid')
      : t('settings.account.danger.logoutAllError')
    : null;

  const deleteError = del.isError
    ? axios.isAxiosError(del.error) && del.error.response?.status === 409
      ? t('settings.account.danger.deletePlanActive')
      : axios.isAxiosError(del.error) && del.error.response?.status === 401
        ? t('settings.account.danger.reauthInvalid')
        : t('settings.account.danger.deleteError')
    : null;

  const canLogoutAll = logoutPassword.length > 0;
  const canDelete =
    deleteText.trim() === email && email.length > 0 && deletePassword.length > 0;

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <CardTitle className="text-lg">{t('settings.account.danger.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Log out of all devices */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{t('settings.account.danger.logoutAllTitle')}</p>
              <p className="text-xs text-muted-foreground">
                {t('settings.account.danger.logoutAllHint')}
              </p>
            </div>
            {!logoutConfirm && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="account-logout-all"
                onClick={() => setLogoutConfirm(true)}
              >
                <LogOut className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {t('settings.account.danger.logoutAll')}
              </Button>
            )}
          </div>
          {logoutConfirm && (
            <div className="space-y-2 rounded-control border border-destructive/40 bg-destructive/5 p-3">
              <Label htmlFor="account-logout-password" className="text-xs">
                {t('settings.account.danger.reauthPrompt')}
              </Label>
              <Input
                id="account-logout-password"
                data-testid="account-logout-password"
                type="password"
                autoComplete="current-password"
                value={logoutPassword}
                placeholder={t('settings.account.danger.reauthPlaceholder')}
                onChange={(e) => setLogoutPassword(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setLogoutConfirm(false);
                    setLogoutPassword('');
                  }}
                  disabled={logoutAll.isPending}
                >
                  {t('settings.account.danger.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  data-testid="account-logout-all-confirm"
                  onClick={() => logoutAll.mutate()}
                  disabled={!canLogoutAll || logoutAll.isPending}
                >
                  {logoutAll.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    t('settings.account.danger.logoutAllConfirm')
                  )}
                </Button>
              </div>
              {logoutError && (
                <p role="alert" className="text-xs text-destructive">
                  {logoutError}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="h-px bg-border" />

        {/* Delete account */}
        <div className="space-y-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('settings.account.danger.deleteTitle')}</p>
            <p className="text-xs text-muted-foreground">
              {t('settings.account.danger.deleteHint')}
            </p>
            <p className="mt-1 text-xs font-medium text-destructive">
              {t('settings.account.danger.deletePlanNote')}
            </p>
          </div>

          {!deleteConfirm ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              data-testid="account-delete"
              onClick={() => setDeleteConfirm(true)}
            >
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t('settings.account.danger.delete')}
            </Button>
          ) : (
            <div className="space-y-2 rounded-control border border-destructive/40 bg-destructive/5 p-3">
              <Label htmlFor="account-delete-input" className="text-xs">
                {t('settings.account.danger.deleteConfirmPrompt', { email })}
              </Label>
              <Input
                id="account-delete-input"
                data-testid="account-delete-input"
                value={deleteText}
                autoComplete="off"
                placeholder={email}
                onChange={(e) => setDeleteText(e.target.value)}
              />
              <Label htmlFor="account-delete-password" className="text-xs">
                {t('settings.account.danger.reauthPrompt')}
              </Label>
              <Input
                id="account-delete-password"
                data-testid="account-delete-password"
                type="password"
                autoComplete="current-password"
                value={deletePassword}
                placeholder={t('settings.account.danger.reauthPlaceholder')}
                onChange={(e) => setDeletePassword(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDeleteConfirm(false);
                    setDeleteText('');
                    setDeletePassword('');
                  }}
                  disabled={del.isPending}
                >
                  {t('settings.account.danger.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  data-testid="account-delete-confirm"
                  onClick={() => del.mutate()}
                  disabled={!canDelete || del.isPending}
                >
                  {del.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    t('settings.account.danger.deleteConfirm')
                  )}
                </Button>
              </div>
              {deleteError && (
                <p role="alert" className="text-xs text-destructive">
                  {deleteError}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
